import { useRef, useState } from 'react';
import { ApiError } from '../../lib/api';
import { requestOtp, verifyOtp } from '../../lib/api/auth';
import type { AuthUser } from '../../lib/auth/types';
import { useFocusOnMount } from '../../lib/hooks/useFocusOnMount';
import { Button } from '../Button';
import { BackLink } from './BackLink';
import { FormError } from '../form/FormError';
import { OtpInput } from '../form/OtpInput';
import { ResendCooldownButton } from '../form/ResendCooldownButton';
import { RegisterLayout } from './RegisterLayout';

const OTP_LENGTH = 6;

/**
 * Step 3 — passcode entry (Figma /reg-3).
 *
 * Verifies the OTP, then forwards the authenticated user to the parent so
 * the parent can decide the next step (PENDING → profile; ACTIVE → /play).
 *
 * Resend uses a 15s tapper. The tapper's countdown survives back-and-forth
 * navigation between this step and the email step via the cooldownUntilRef.
 */
type CodeStepProps = {
  email: string;
  onBack: () => void;
  onVerified: (user: AuthUser) => void;
  /** Persisted across mounts so the resend cooldown doesn't reset on
   *  back-then-forward navigation. Parent owns the ref. */
  resendCooldownUntilRef: { current: number };
};

export function CodeStep({
  email,
  onBack,
  onVerified,
  resendCooldownUntilRef,
}: CodeStepProps) {
  const [code, setCode] = useState('');
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [bannerError, setBannerError] = useState<string | null>(null);
  // Split the in-flight state so we can drive distinct button labels
  // ("Verifying…" only fires during verify, not during resend), but every
  // button on the step is disabled whenever ANY of these are true.
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [transitioning, setTransitioning] = useState(false);
  const busy = verifying || resending || transitioning;
  const inputRef = useRef<HTMLInputElement>(null);
  const headingRef = useFocusOnMount<HTMLHeadingElement>();

  async function handleSubmit(): Promise<void> {
    if (busy) return;
    if (code.length !== OTP_LENGTH) {
      setFieldError(`Enter the ${OTP_LENGTH}-digit code we emailed you.`);
      return;
    }
    setFieldError(null);
    setBannerError(null);
    setVerifying(true);
    try {
      const data = await verifyOtp({ email, code });
      // Hand off to parent — keep `transitioning` true so all buttons stay
      // disabled through the AnimatePresence exit fade.
      setTransitioning(true);
      onVerified(data.user);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setFieldError('That code is invalid or expired. Try resending.');
      } else if (err instanceof ApiError && err.status === 429) {
        setBannerError('Too many attempts. Wait a few minutes and try again.');
      } else if (err instanceof ApiError && err.status >= 500) {
        setBannerError('Something went wrong on our end. Please try again.');
      } else {
        setBannerError('Network error. Check your connection and try again.');
      }
    } finally {
      setVerifying(false);
    }
  }

  async function handleResend(): Promise<void> {
    if (busy) return;
    setBannerError(null);
    setResending(true);
    try {
      await requestOtp(email, true);
      setCode('');
      inputRef.current?.focus();
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        setBannerError("You're requesting codes too quickly. Wait a moment.");
      } else {
        setBannerError("Couldn't resend the code. Please try again.");
      }
    } finally {
      setResending(false);
    }
  }

  return (
    <RegisterLayout>
      <BackLink onClick={onBack} disabled={busy} />
      <div className="flex flex-col gap-[6px] text-center">
        <h2
          ref={headingRef}
          tabIndex={-1}
          className="font-['Orbitron',sans-serif] text-[clamp(16px,4.8vw,22px)] uppercase tracking-[0.1em] text-white focus:outline-none"
        >
          Check your email
        </h2>
        <p className="font-['Arial',sans-serif] text-[14px] text-white/80">
          We sent a one-time passcode to{' '}
          <span className="text-white [overflow-wrap:anywhere]">{email}</span>.
        </p>
      </div>

      <form
        className="flex flex-col items-stretch gap-[16px]"
        onSubmit={(e) => {
          e.preventDefault();
          void handleSubmit();
        }}
        noValidate
      >
        <OtpInput
          ref={inputRef}
          value={code}
          onChange={(v) => {
            setCode(v);
            if (fieldError) setFieldError(null);
          }}
          length={OTP_LENGTH}
          error={fieldError}
          onSubmit={() => void handleSubmit()}
        />

        <div className="flex flex-col items-center gap-[8px]">
          <ResendCooldownButton
            onResend={handleResend}
            cooldownSeconds={15}
            cooldownUntilRef={resendCooldownUntilRef}
            // Disable during ANY in-flight state on this step (verify, resend
            // already in flight via the internal guard, or the outgoing
            // transition). The button still owns its own cooldown timer +
            // internal submitting guard on top of this.
            disabled={busy}
          />
          {/*
            Escape hatch for users who never receive the email — typo'd it,
            bounced previously (canEmail=false), wrong inbox, etc. Going
            "Back" returns them to the email step where they can edit the
            address; this is the same action with a clearer label. Styled
            to mirror the Resend Passcode link.
          */}
          <button
            type="button"
            onClick={onBack}
            disabled={busy}
            className={[
              "font-['Michroma','Orbitron',sans-serif] text-[11px] uppercase tracking-[1.1px] underline-offset-4",
              busy
                ? 'cursor-not-allowed text-white/40 no-underline'
                : 'text-[#00ff8b] underline hover:text-[#7afcc6]',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#3a2f44]',
            ].join(' ')}
          >
            Use a Different Address
          </button>
        </div>

        <FormError>{bannerError}</FormError>

        <div className="flex flex-col items-center gap-[16px]">
          <Button
            variant="dark"
            type="submit"
            disabled={busy || code.length !== OTP_LENGTH}
            className="min-w-[200px]"
          >
            {verifying ? 'Verifying…' : 'Next'}
          </Button>
        </div>
      </form>
    </RegisterLayout>
  );
}
